import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, AlertCircle } from "lucide-react";
import {
  sendMessage,
  sendMediaMessage,
  checkWindowStatus,
  downloadMedia,
  uploadChatMedia,
  getChatAiStatus,
  toggleChatAiStatus,
} from "../apis/chat";
import { getSocket } from "../utils/socket";
import { useLiveChat } from "../contexts/LiveChatContext";
import ChatList from "../components/livechat/ChatList";
import ChatHeader from "../components/livechat/ChatHeader";
import MessageList from "../components/livechat/MessageList";
import MessageInput from "../components/livechat/MessageInput";

/**
 * LiveChats Component - Main component for live chat functionality
 * Handles chat selection, message sending, media uploads, and AI toggling
 */
const LiveChats = () => {
  // Get state and functions from context
  const {
    selectedChat,
    messages,
    loading,
    messagesLoading,
    selectChat,
    updateMessage,
    chats,
  } = useLiveChat();

  // Local UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(true);
  const [windowWarning, setWindowWarning] = useState(false);
  const [downloadingMedia, setDownloadingMedia] = useState(new Set());
  const [chatAiEnabled, setChatAiEnabled] = useState(false);
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const messagesContainerRef = useRef(null);

  /**
   * Initialize socket for media download completion events
   */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) {
      console.warn("Socket not available");
      return;
    }

    // Listen for media download completion
    socket.on("media_downloaded", (data) => {
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(data.message_id);
        return next;
      });
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.off("media_downloaded");
      }
    };
  }, []);

  /**
   * Fetch AI status for a chat
   */
  const fetchChatAiStatus = async (customerPhone, phoneNumberId) => {
    try {
      const response = await getChatAiStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setChatAiEnabled(response.data.data.ai_enabled || false);
      }
    } catch (error) {
      console.error("Error fetching chat AI status:", error);
      setChatAiEnabled(false);
    }
  };

  /**
   * Toggle AI status for a chat
   */
  const handleToggleChatAi = async () => {
    if (!selectedChat || aiToggleLoading) return;
    
    const newStatus = !chatAiEnabled;
    setAiToggleLoading(true);
    
    try {
      const response = await toggleChatAiStatus(
        selectedChat.customer_phone,
        selectedChat.phone_number_id,
        newStatus
      );
      
      if (response.data.success) {
        setChatAiEnabled(newStatus);
      } else {
        console.error("Failed to toggle AI status");
      }
    } catch (error) {
      console.error("Error toggling chat AI status:", error);
    } finally {
      setAiToggleLoading(false);
    }
  };

  /**
   * Handle chat selection
   * Messages are marked as read in selectChat function in context
   */
  const handleChatSelect = async (chat) => {
    if (!chat.customer_phone || !chat.phone_number_id) {
      console.error("Missing chat data:", chat);
      return;
    }
    
    // Set window status from chat data (from whatsapp_contacts table)
    // This is the most up-to-date value from the conversations list
    setIsWindowOpen(chat.is_window_open !== undefined ? chat.is_window_open : true);
    
    // Select chat (this will mark messages as read and fetch messages via context)
    selectChat(chat);
    
    // Fetch AI status for this chat
    await fetchChatAiStatus(chat.customer_phone, chat.phone_number_id);
    
    // Check window status (this will update isWindowOpen if needed)
    // The window status from getMessages API response will also update it
    checkConversationWindow(chat.customer_phone, chat.phone_number_id);
    
    // Scroll to bottom after messages load
    setTimeout(() => scrollToBottom(), 100);
  };

  /**
   * Update window status when selectedChat changes or chats update
   */
  useEffect(() => {
    if (selectedChat) {
      // Find the chat in the chats list to get the latest window status
      const currentChat = chats.find(
        (c) =>
          c.customer_phone === selectedChat.customer_phone &&
          c.phone_number_id === selectedChat.phone_number_id
      );
      if (currentChat && currentChat.is_window_open !== undefined) {
        setIsWindowOpen(currentChat.is_window_open);
      }
    }
  }, [selectedChat, chats]);

  /**
   * Check conversation window status
   */
  const checkConversationWindow = async (customerPhone, phoneNumberId) => {
    try {
      const response = await checkWindowStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setIsWindowOpen(response.data.data.is_window_open);
      }
    } catch (error) {
      console.error("Error checking window status:", error);
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert("File size must be less than 50MB");
      return;
    }

    setSelectedFile(file);

    // Create preview
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "image", url: e.target.result });
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "video", url: e.target.result });
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("audio/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "audio", url: e.target.result });
      reader.readAsDataURL(file);
    } else {
      setFilePreview({ type: "document", name: file.name, size: file.size });
    }

    // Reset file input
    e.target.value = "";
  };

  /**
   * Remove selected file
   */
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  /**
   * Send message (text or media)
   */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!messageText.trim() && !selectedFile) || !selectedChat || sending || uploadingMedia) return;

    // Check if window is open
    if (!isWindowOpen) {
      setWindowWarning(true);
      setTimeout(() => setWindowWarning(false), 5000);
      return;
    }

    try {
      setSending(true);

      if (selectedFile) {
        // Upload file first
        setUploadingMedia(true);
        const uploadResponse = await uploadChatMedia(selectedFile);
        
        if (uploadResponse.data.success) {
          // Send media message
          const response = await sendMediaMessage(
            selectedChat.phone_number_id,
            selectedChat.customer_phone,
            uploadResponse.data.data.media_url,
            uploadResponse.data.data.media_type,
            messageText.trim() || null // Use messageText as caption if provided
          );

          if (response.data.success) {
            setMessageText("");
            setSelectedFile(null);
            setFilePreview(null);
          }
        }
        setUploadingMedia(false);
      } else {
        // Send text message
        const response = await sendMessage(
          selectedChat.phone_number_id,
          selectedChat.customer_phone,
          messageText
        );

        if (response.data.success) {
          setMessageText("");
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (error.response?.data?.error === "CONVERSATION_WINDOW_CLOSED") {
        setWindowWarning(true);
        setIsWindowOpen(false);
        setTimeout(() => setWindowWarning(false), 5000);
      } else {
        alert(error.response?.data?.error || "Failed to send message. Please try again.");
      }
    } finally {
      setSending(false);
      setUploadingMedia(false);
    }
  };

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  /**
   * Scroll to bottom when messages change
   */
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  /**
   * Handle media download
   */
  const handleDownloadMedia = async (message, isComplete = false) => {
    if (isComplete) {
      // Just clear downloading state
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(message.message_id);
        return next;
      });
      return;
    }

    // Check if we have media_id or media_url, and not already downloading
    if (!selectedChat || (!message.media_id && !message.media_url) || downloadingMedia.has(message.message_id)) {
      return;
    }

    // If already has downloaded URL, no need to download again
    if (message.downloaded_media_url) {
      return;
    }

    try {
      setDownloadingMedia((prev) => new Set(prev).add(message.message_id));

      // Call backend API to download media
      const response = await downloadMedia(message.message_id, selectedChat.phone_number_id);
      
      if (response.data.success && response.data.data.downloaded_media_url) {
        const downloadedUrl = response.data.data.downloaded_media_url;
        
        // Update message with downloaded URL
        updateMessage(message.message_id, { 
          downloaded_media_url: downloadedUrl,
        });
        
        // Clear downloading state
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      } else {
        // If no URL returned, clear downloading state
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      }
    } catch (error) {
      console.error("❌ Error downloading media:", error);
      alert("Failed to download media. Please try again.");
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(message.message_id);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 sm:px-4 lg:px-5 py-4">
        {/* Window Warning */}
        {windowWarning && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertCircle size={16} className="text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Conversation window is closed. You can only send messages within 24 hours of the customer's last message. Please use a template message instead.
            </p>
          </div>
        )}

        {/* Main Chat Container */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex h-[calc(100vh-12rem)]">
            {/* Chat List Sidebar */}
            <ChatList
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onChatSelect={handleChatSelect}
            />

            {/* Chat Window */}
            <div className="hidden md:flex flex-1 flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <ChatHeader
                    selectedChat={selectedChat}
                    isWindowOpen={isWindowOpen}
                    chatAiEnabled={chatAiEnabled}
                    aiToggleLoading={aiToggleLoading}
                    onToggleChatAi={handleToggleChatAi}
                  />

                  {/* Messages Area */}
                  <MessageList
                    downloadingMedia={downloadingMedia}
                    onDownloadMedia={handleDownloadMedia}
                    messagesContainerRef={messagesContainerRef}
                  />

                  {/* Message Input */}
                  <MessageInput
                    messageText={messageText}
                    setMessageText={setMessageText}
                    selectedFile={selectedFile}
                    filePreview={filePreview}
                    isWindowOpen={isWindowOpen}
                    sending={sending}
                    uploadingMedia={uploadingMedia}
                    onFileSelect={handleFileSelect}
                    onRemoveFile={handleRemoveFile}
                    onSendMessage={handleSendMessage}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveChats;
